// Test & Refine redesign - Results view.
//
// The payoff. Everything here binds to the real service types (BeforeAfterData,
// ModelResult[], CompetitorResult[], TimeToImpact[], discoveries, rationale).
// Sections whose backing data is empty are hidden rather than faked. The
// percentile spread is labeled "Range of likely outcomes" (never "Monte
// Carlo"). No em dashes in prose.
import { useRef, useState, type ReactNode, type CSSProperties } from "react";
import {
  ArrowRight,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  Info,
  BarChart3,
  Shuffle,
  Table2,
  Wrench,
  Layers,
  Clock,
} from "lucide-react";
import { getModelColor } from "@/app/constants/models";
import { MetricTerm } from "@/app/components/metric-term";
import type { AISandboxData } from "@/app/services/simulation/ai-sandbox-service";
import type { MonitoringSource } from "./test-refine-shell";
import { TrOrb, Pill, GroupLabel, discoveryConfig, signedPp, clampPctWidth } from "./shared";

// Generic, run-agnostic starters (no hardcoded model or action names).
const ASK_CHIPS = [
  "Which change drove the most lift?",
  "Why did some models barely move?",
  "How confident are these estimates?",
];

export function ResultsView({
  sandboxData,
  query,
  brand,
  appliedQueueItems,
  monitoringSource,
  selectedModelCount,
  onOpenAIChat,
}: {
  sandboxData: AISandboxData;
  query: string;
  brand: string;
  appliedQueueItems: Array<{ id: number }>;
  monitoringSource: MonitoringSource;
  selectedModelCount: number;
  onOpenAIChat: (topic?: string) => void;
}) {
  const ba = sandboxData.beforeAfterData;
  const models = sandboxData.modelResults || [];
  const discoveries = sandboxData.discoveries || [];
  const rationale = sandboxData.rationale || { issues: [], gaps: [], opportunities: [] };
  const competitors = sandboxData.competitors || [];
  const timeToImpact = sandboxData.timeToImpact || [];

  const evidenceRef = useRef<HTMLDivElement | null>(null);
  const discoveriesRef = useRef<HTMLDivElement | null>(null);
  const planRef = useRef<HTMLDivElement | null>(null);

  const [showAllDiscoveries, setShowAllDiscoveries] = useState(false);
  const DISCOVERY_PREVIEW = 3;

  const [askValue, setAskValue] = useState("");
  const askHasText = askValue.trim().length > 0;
  const submitAsk = () => {
    const q = askValue.trim();
    onOpenAIChat(q.length ? q : undefined);
    setAskValue("");
  };

  const beforeP = Math.round(ba?.before?.avgProbability ?? 0);
  const afterP = Math.round(ba?.after?.avgProbability ?? 0);
  const delta = Math.round(ba?.delta?.probability ?? afterP - beforeP);
  const beforeRank = ba?.before?.topRank ?? 0;
  const afterRank = ba?.after?.topRank ?? 0;
  // All-zero estimates mean there is no monitoring baseline yet: show a
  // pre-baseline state, not "0 points more often". Rank claims are only honest
  // when both ranks are real (a rank of 0 or less means no baseline rank
  // exists, and pre-baseline placeholder ranks are not real either).
  const preBaseline = beforeP === 0 && afterP === 0 && delta === 0;
  const hasRanks = !preBaseline && beforeRank > 0 && afterRank > 0;
  const actionCount = appliedQueueItems.length;
  const displayQuery = query.trim() || "your query";
  const brandLabel = brand || "your brand";

  const honesty = honestyCopy(monitoringSource, sandboxData.monitoringBeforeUsed);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── HERO ─────────────────────────────────────────────── */}
      <div style={{ border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--elevation-md)" }}>
        <div style={{ height: 4, background: "linear-gradient(90deg, var(--alpha-teal), var(--spectral-violet))" }} />
        <div style={{ padding: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <TrOrb size={32} radius={9} iconSize={15} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--spectral-violet)" }}>Sandbox Simulation Results</div>
              <div style={{ fontSize: 11.5, color: "var(--muted-foreground)" }}>
                {selectedModelCount} models · {actionCount} {actionCount === 1 ? "action" : "actions"} simulated
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
              <Pill
                color={delta < 0 ? "var(--infrared-coral)" : "var(--signal-green)"}
                bg={delta < 0 ? "var(--infrared-coral-10)" : "var(--signal-green-10)"}
                style={{ fontWeight: 700, fontSize: 12 }}
              >
                {delta < 0 ? <TrendingDown size={12} strokeWidth={2.2} /> : <TrendingUp size={12} strokeWidth={2.2} />}
                {signedPp(delta)}
              </Pill>
              <span style={{ fontSize: 11.5, whiteSpace: "nowrap" }}>
                <MetricTerm
                  definition="These before and after numbers are AI estimates, not measurements. Nothing is shipped or measured here. They reflect how confidently AI models would recommend your brand for this exact question right now, and how confidently they would if you shipped the selected changes."
                  calculation="Each AI model you selected reads your brand, this buyer question, your current Brand Monitoring baseline, and the specific changes you queued. It then judges two things: how likely it is to recommend you for this question today, and how likely it would be if those changes were live. We do that across every model and average the results. No confidence band is shown: these are the models' own estimates, not a measurement, so there is no sampling error to put a band around."
                  interpretation="Use this to prioritize which changes to ship, not as a promise. A larger estimated lift means the models see these changes as more likely to move your standing for this question. Because AI recommendations lean on third-party consensus and authority, your own site changes usually move things modestly. Ship the changes, then re-run Brand Monitoring to measure the real movement."
                  onOpenAIChat={() => onOpenAIChat("How are the Test & Refine simulation estimates calculated?")}
                >
                  How are these estimates calculated?
                </MetricTerm>
              </span>
            </div>
          </div>

          <p
            style={{
              fontSize: 18,
              fontWeight: 500,
              lineHeight: 1.5,
              letterSpacing: "-.01em",
              color: "var(--foreground)",
              margin: "0 0 20px",
              textWrap: "pretty",
            }}
          >
            {preBaseline ? (
              <>
                This simulation of <strong style={{ fontWeight: 600 }}>{actionCount} {actionCount === 1 ? "action" : "actions"}</strong> for{" "}
                <strong style={{ fontWeight: 600 }}>{brandLabel}</strong> has no Brand Monitoring baseline to measure against yet.
                Run your first measurement, then simulate again to see the estimated movement for "{displayQuery}".
              </>
            ) : (
              <>
                If you ship these <strong style={{ fontWeight: 600 }}>{actionCount} {actionCount === 1 ? "action" : "actions"}</strong>, AI
                recommends <strong style={{ fontWeight: 600 }}>{brandLabel}</strong> for "{displayQuery}"{" "}
                <strong style={{ color: delta < 0 ? "var(--infrared-coral)" : "var(--teal-text)", fontWeight: 600 }}>
                  {Math.abs(delta)} points {delta < 0 ? "less" : "more"} often
                </strong>
                {hasRanks && (
                  <>
                    , moving you from rank #{beforeRank} to <strong style={{ color: "var(--teal-text)", fontWeight: 600 }}>#{afterRank}</strong>
                  </>
                )}.
              </>
            )}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 14, alignItems: "stretch" }} className="tr-grid-3">
            <div style={{ background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 12, padding: "16px 18px" }}>
              <div style={eyebrow}>Before</div>
              <div style={{ fontSize: 34, fontWeight: 800, color: "var(--muted-foreground)", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                {beforeP}%
              </div>
              <div style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 6 }}>
                recommendation rate{hasRanks ? <> · rank #{beforeRank}</> : null}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <ArrowRight size={22} strokeWidth={2} style={{ color: "var(--foreground)" }} />
              <Pill color="var(--teal-text)" bg="var(--alpha-teal-10)">
                {actionCount} {actionCount === 1 ? "action" : "actions"}
              </Pill>
            </div>
            <div style={{ background: "var(--alpha-teal-5)", border: "1px solid var(--alpha-teal-20)", borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ ...eyebrow, color: "var(--teal-text)" }}>After</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <div style={{ fontSize: 34, fontWeight: 800, color: "var(--teal-text)", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                  {afterP}%
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: delta < 0 ? "var(--infrared-coral)" : "var(--signal-green)" }}>{signedPp(delta)}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 6 }}>
                expected rate{hasRanks ? <> · rank <strong style={{ color: "var(--teal-text)" }}>#{afterRank}</strong></> : null}
              </div>
            </div>
          </div>

          {/* Honesty line (conditional, ruling #8) */}
          <div style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 14, display: "flex", alignItems: "center", gap: 6 }}>
            <Info size={13} strokeWidth={1.7} style={{ flexShrink: 0 }} />
            {honesty}
          </div>
        </div>
      </div>

      {/* ── Ask Upmode bar (pinned under hero) ────────────────── */}
      <div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitAsk();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 14,
            padding: "0 12px 0 16px",
            height: 58,
            boxShadow: "0 0 0 4px var(--spectral-violet-10), 0 10px 26px -16px rgba(126, 95, 224, 0.32)",
            marginBottom: 12,
          }}
        >
          <TrOrb size={32} radius={10} iconSize={15} animate={false} style={{ boxShadow: "var(--shadow-spectral-violet-glow)" }} />
          <input
            className="tr-input"
            type="text"
            value={askValue}
            onChange={(e) => setAskValue(e.target.value)}
            placeholder={'Ask anything about these results: "Why didn\'t Gemini move?"'}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 15,
              fontFamily: "var(--font-sans)",
              color: "var(--foreground)",
            }}
          />
          {askHasText && (
            <button
              type="submit"
              aria-label="Ask"
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "linear-gradient(135deg, var(--spectral-violet), var(--signal-blue))",
                color: "var(--on-orb)",
                border: "none",
                cursor: "pointer",
              }}
            >
              <ArrowRight size={15} />
            </button>
          )}
        </form>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>Not sure where to start?</span>
          {ASK_CHIPS.map((q) => (
            <button
              key={q}
              className="tr-chip"
              onClick={() => onOpenAIChat(q)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                height: 32,
                padding: "0 13px",
                borderRadius: 9999,
                background: "var(--card)",
                border: "1px solid var(--border)",
                fontSize: 12,
                fontWeight: 500,
                color: "var(--foreground)",
                cursor: "pointer",
              }}
            >
              <span style={{ width: 13, height: 13, borderRadius: 9999, background: "linear-gradient(135deg, var(--spectral-violet), var(--signal-blue))", flexShrink: 0 }} />
              {q}
            </button>
          ))}
        </div>
      </div>

      {/* ── The evidence ──────────────────────────────────────── */}
      <GroupLabel>The evidence</GroupLabel>

      {models.length > 0 && (
        <div ref={evidenceRef}>
          <Card>
            <div style={{ padding: "20px 22px" }}>
              <div style={sectionLabel}>Per-model breakdown</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {models.map((m) => {
                  const lift = Math.round(m.after - m.before);
                  return (
                    <div key={m.id || m.name}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--foreground)" }}>{m.name}</span>
                        <span style={{ fontSize: 12.5, color: "var(--muted-foreground)", fontVariantNumeric: "tabular-nums" }}>
                          {Math.round(m.before)}%{" "}
                          <ArrowRight size={11} strokeWidth={2} style={{ display: "inline", verticalAlign: "middle" }} />{" "}
                          <strong style={{ color: "var(--teal-text)" }}>{Math.round(m.after)}%</strong>{" "}
                          <span style={{ color: lift < 0 ? "var(--infrared-coral)" : "var(--signal-green)", fontWeight: 700 }}>{lift >= 0 ? "+" : ""}{lift}</span>
                        </span>
                      </div>
                      <div style={{ position: "relative", height: 8, borderRadius: 9999, background: "var(--muted)", overflow: "hidden" }}>
                        <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: clampPctWidth(m.before), borderRadius: 9999, background: "var(--silver-mist)", transition: "width .7s var(--ease-standard)" }} />
                        <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: clampPctWidth(m.after), borderRadius: 9999, background: "linear-gradient(90deg, var(--alpha-teal), var(--spectral-violet))", transition: "width .9s var(--ease-standard)" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>
      )}

      {discoveries.length > 0 && (
        <div ref={discoveriesRef}>
          <Card>
            <div style={{ padding: "20px 22px" }}>
              <div style={sectionLabel}>What the simulation found</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {(showAllDiscoveries ? discoveries : discoveries.slice(0, DISCOVERY_PREVIEW)).map((d, i) => {
                  const cfg = discoveryConfig(d.tag);
                  return (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        gap: 12,
                        padding: "13px 15px",
                        borderRadius: 10,
                        background: cfg.bg,
                        border: `1px solid ${cfg.border}`,
                      }}
                    >
                      <span style={{ color: cfg.color, flexShrink: 0, marginTop: 1 }}>
                        <Info size={16} strokeWidth={1.9} />
                      </span>
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--foreground)", marginBottom: 3 }}>{d.title}</div>
                        <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.55, margin: 0 }}>{d.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {discoveries.length > DISCOVERY_PREVIEW && (
                <button
                  onClick={() => setShowAllDiscoveries((v) => !v)}
                  style={{
                    marginTop: 12,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "var(--spectral-violet)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {showAllDiscoveries
                    ? "Show less"
                    : `Show ${discoveries.length - DISCOVERY_PREVIEW} more`}
                  <ChevronDown
                    size={15}
                    strokeWidth={2}
                    className="tr-chevron"
                    style={{ transform: showAllDiscoveries ? "rotate(180deg)" : "none" }}
                  />
                </button>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Model estimates, not measurements (#132). The effect size,
          significance, confidence range and Monte Carlo range were the
          models' invention, so they are no longer shown. */}
      <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "14px 18px", background: "var(--muted)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>
          <BarChart3 size={15} strokeWidth={1.8} /> Model estimates, no band
        </div>
        <p style={{ fontSize: 12.5, color: "var(--muted-foreground)", lineHeight: 1.55, margin: "6px 0 0" }}>
          {sandboxData.estimateNote
            || "These figures are each model's own guess at how AI would answer after these changes. They are not a measurement, and no confidence band applies."}
          {" "}To measure a change before it ships, test it in the wind tunnel (ask your Upmode team, or open Wind tunnel in the admin tools).
        </p>
      </div>

      {/* Competitor benchmark (ruling #5) */}
      {competitors.length > 0 && (
        <Disclosure icon={<Shuffle size={15} strokeWidth={1.8} />} title="Competitor benchmark" subtitle="Where you land in the ranked field after your changes">
          <CompetitorInner competitors={competitors} brand={brand} afterP={afterP} beforeP={beforeP} />
        </Disclosure>
      )}

      {/* Raw data */}
      {models.length > 0 && (
        <Disclosure icon={<Table2 size={15} strokeWidth={1.8} />} title="Raw data" subtitle="Per-model estimates: before, after, lift, rank">
          <RawInner models={models} />
        </Disclosure>
      )}

      {/* Technical details */}
      {(rationale.issues.length > 0 || rationale.gaps.length > 0 || rationale.opportunities.length > 0) && (
        <Disclosure icon={<Wrench size={15} strokeWidth={1.8} />} title="Technical details" subtitle="Issues fixed, gaps addressed, and opportunities">
          <TechInner rationale={rationale} />
        </Disclosure>
      )}

      {/* ── The plan ──────────────────────────────────────────── */}
      {(rationale.opportunities.length > 0 || timeToImpact.length > 0) && <GroupLabel>The plan</GroupLabel>}

      {rationale.opportunities.length > 0 && (
        <div ref={planRef}>
          <Card>
            <div style={{ padding: "20px 22px" }}>
              <div style={sectionLabel}>Recommended next steps</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {rationale.opportunities.map((step, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 9999,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        background: "var(--spectral-violet-10)",
                        color: "var(--spectral-violet)",
                      }}
                    >
                      {i + 1}
                    </span>
                    <span style={{ flex: 1, fontSize: 13.5, color: "var(--foreground)", lineHeight: 1.4 }}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      )}

      {timeToImpact.length > 0 && (
        <>
          <Disclosure icon={<Layers size={15} strokeWidth={1.8} />} title="Deploy by platform" subtitle="Where to publish each change and when it lands">
            <DeployInner items={timeToImpact} />
          </Disclosure>
          <Disclosure icon={<Clock size={15} strokeWidth={1.8} />} title="Time-to-impact per platform" subtitle="How long each model takes to reflect your changes">
            <TimeInner items={timeToImpact} />
          </Disclosure>
        </>
      )}
    </div>
  );
}

// ── Honesty copy (ruling #8) ──────────────────────────────────────
function honestyCopy(source: MonitoringSource, used?: boolean): string {
  if (source === "live" && used !== false) {
    return "Before scores from your live Brand Monitoring baseline. After scores are AI estimates, not guarantees.";
  }
  if (source === "cached" && used !== false) {
    return "Before scores from your last saved Brand Monitoring run. After scores are AI estimates, not guarantees.";
  }
  return "Run Brand Monitoring first for real before-scores. These are AI estimates, not guarantees.";
}

// ── Disclosure ────────────────────────────────────────────────────
function Disclosure({ icon, title, subtitle, children }: { icon: ReactNode; title: string; subtitle: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "16px 22px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
        }}
      >
        <span style={{ width: 30, height: 30, borderRadius: 8, background: "var(--spectral-violet-10)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--spectral-violet)", flexShrink: 0 }}>
          {icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--foreground)" }}>{title}</div>
          <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>{subtitle}</div>
        </div>
        <ChevronDown size={18} strokeWidth={2} className="tr-chevron" style={{ color: "var(--muted-foreground)", transform: open ? "rotate(180deg)" : "none", flexShrink: 0 }} />
      </button>
      {open && <div style={{ padding: "0 22px 22px" }} className="tr-fade">{children}</div>}
    </Card>
  );
}

// ── Competitor inner (ruling #5) ──────────────────────────────────
function CompetitorInner({
  competitors,
  brand,
  afterP,
  beforeP,
}: {
  competitors: NonNullable<AISandboxData["competitors"]>;
  brand: string;
  afterP: number;
  beforeP: number;
}) {
  const brandProb = competitors[0]?.after_brand_probability ?? afterP;
  const brandBefore = competitors[0]?.before_probability ?? beforeP;
  const brandDelta = Math.round(brandProb - brandBefore);

  type Row = {
    name: string;
    p: number;
    you: boolean;
    delta?: number;
    gapBefore?: number;
    gapAfter?: number;
    closable?: boolean;
  };

  const rows: Row[] = [
    { name: brand || "You", p: brandProb, you: true, delta: brandDelta },
    ...competitors.map((c) => ({
      name: c.name,
      p: c.competitor_probability,
      you: false,
      gapBefore: c.gap_before,
      gapAfter: c.gap_after,
      closable: c.is_closable,
    })),
  ].sort((a, b) => b.p - a.p);

  const maxP = Math.max(1, ...rows.map((r) => r.p));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
      {rows.map((r) => (
        <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 96,
              flexShrink: 0,
              fontSize: 13,
              fontWeight: r.you ? 600 : 500,
              color: r.you ? "var(--teal-text)" : "var(--foreground)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {r.name}
            {r.you && (
              <span style={{ fontSize: 9, marginLeft: 5, textTransform: "uppercase", letterSpacing: ".05em" }}>You</span>
            )}
          </span>
          <div style={{ flex: 1, height: 14, borderRadius: 9999, background: "var(--muted)", overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                borderRadius: 9999,
                width: clampPctWidth((r.p / maxP) * 100),
                background: r.you ? "linear-gradient(90deg, var(--alpha-teal), var(--spectral-violet))" : "var(--silver-mist)",
                transition: "width .8s var(--ease-standard)",
              }}
            />
          </div>
          {/* gap + closable (competitor rows) or delta (brand row) */}
          <span style={{ width: 150, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 7 }}>
            {r.you ? (
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--foreground)", fontVariantNumeric: "tabular-nums" }}>
                {Math.round(r.p)}%
                {typeof r.delta === "number" && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: r.delta < 0 ? "var(--infrared-coral)" : "var(--signal-green)", marginLeft: 4 }}>{signedPp(r.delta)}</span>
                )}
              </span>
            ) : (
              <>
                {typeof r.gapBefore === "number" && typeof r.gapAfter === "number" && (
                  <span style={{ fontSize: 11, color: "var(--muted-foreground)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {r.gapBefore} → {r.gapAfter} pts
                  </span>
                )}
                {r.closable && (
                  <Pill color="var(--teal-text)" bg="var(--teal-pill-bg)" style={{ fontSize: 10 }}>
                    Closable
                  </Pill>
                )}
              </>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Raw table inner ───────────────────────────────────────────────
function RawInner({ models }: { models: NonNullable<AISandboxData["modelResults"]> }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
        <thead>
          <tr>
            {["Model", "Before", "After", "Lift", "Rank", "Time"].map((h, i) => (
              <th key={h} style={{ textAlign: i === 0 ? "left" : "right", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--muted-foreground)", padding: "0 0 10px" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {models.map((m) => (
            <tr key={m.id || m.name}>
              <Td align="left"><span style={{ fontWeight: 600 }}>{m.name}</span></Td>
              <Td color="var(--muted-foreground)">{Math.round(m.before)}%</Td>
              <Td color="var(--teal-text)" weight={600}>{Math.round(m.after)}%</Td>
              <Td color={m.after - m.before < 0 ? "var(--infrared-coral)" : "var(--signal-green)"} weight={700}>{m.after - m.before >= 0 ? "+" : ""}{Math.round(m.after - m.before)}</Td>
              <Td>#{m.beforeRank}→#{m.afterRank}</Td>
              <Td color="var(--muted-foreground)">{m.responseTime || "–"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Td({ children, align = "right", color, weight }: { children: ReactNode; align?: "left" | "right"; color?: string; weight?: number }) {
  return (
    <td style={{ textAlign: align, fontSize: 12.5, color: color || "var(--foreground)", fontWeight: weight, fontVariantNumeric: "tabular-nums", padding: "9px 0", borderTop: "1px solid var(--border)" }}>
      {children}
    </td>
  );
}

// ── Technical inner (middot bullets, ruling #7) ───────────────────
function TechInner({ rationale }: { rationale: NonNullable<AISandboxData["rationale"]> }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }} className="tr-grid-3">
      <TechCol title="Issues fixed" color="var(--infrared-coral)" items={rationale.issues} />
      <TechCol title="Gaps addressed" color="var(--signal-blue)" items={rationale.gaps} />
      <TechCol title="Opportunities" color="var(--signal-green)" items={rationale.opportunities} />
    </div>
  );
}

function TechCol({ title, color, items }: { title: string; color: string; items: string[] }) {
  return (
    <div style={{ background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color, marginBottom: 10 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {items.map((t, i) => (
          <div key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, color: "var(--foreground)", lineHeight: 1.5 }}>
            <span style={{ color, flexShrink: 0, marginTop: 1 }}>·</span>
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Deploy inner ──────────────────────────────────────────────────
function DeployInner({ items }: { items: NonNullable<AISandboxData["timeToImpact"]> }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((d) => {
        const color = platformColor(d.model_family, d.platform);
        return (
          <div key={d.platform} style={{ border: "1px solid var(--border)", borderLeft: `3px solid ${color}`, borderRadius: 10, padding: "15px 17px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", marginBottom: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)" }}>{d.platform}</span>
              <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color, background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 9999, padding: "2px 9px", whiteSpace: "nowrap" }}>
                {d.estimated_days_min}–{d.estimated_days_max} days
              </span>
            </div>
            <p style={{ fontSize: 13, color: "var(--foreground)", lineHeight: 1.55, margin: "0 0 12px" }}>
              <strong style={{ fontWeight: 600 }}>Action: </strong>
              {d.update_action}
            </p>
            {d.where_to_update.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 10 }}>
                {d.where_to_update.map((w, i) => (
                  <span key={i} style={{ fontSize: 11, fontWeight: 500, color: "var(--muted-foreground)", background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 7, padding: "3px 9px" }}>
                    {w}
                  </span>
                ))}
              </div>
            )}
            <div style={{ fontSize: 11.5, color: "var(--muted-foreground)", display: "flex", gap: 6 }}>
              <Layers size={13} strokeWidth={1.6} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <strong style={{ fontWeight: 600, color: "var(--foreground)" }}>Framework: </strong>
                {d.framework_or_site}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Time-to-impact inner ──────────────────────────────────────────
function TimeInner({ items }: { items: NonNullable<AISandboxData["timeToImpact"]> }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((t) => {
        const color = platformColor(t.model_family, t.platform);
        const pct = Math.max(6, Math.min(100, Math.round((t.estimated_days_max / 30) * 100)));
        return (
          <div key={t.platform} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <span style={{ width: 6, height: 6, borderRadius: 9999, background: color, flexShrink: 0, marginTop: 6 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--foreground)" }}>{t.platform}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color, whiteSpace: "nowrap" }}>
                  {t.estimated_days_min}–{t.estimated_days_max} days
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 9999, background: "var(--muted)", overflow: "hidden", marginBottom: 5 }}>
                <div style={{ height: "100%", borderRadius: 9999, width: `${pct}%`, background: color }} />
              </div>
              <p style={{ fontSize: 12, color: "var(--muted-foreground)", lineHeight: 1.5, margin: 0 }}>{t.why}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Shared bits (local) ───────────────────────────────────────────
function Card({ children }: { children: ReactNode }) {
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 14, background: "var(--card)", boxShadow: "var(--elevation-sm)" }}>
      {children}
    </div>
  );
}

const sectionLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: ".05em",
  color: "var(--muted-foreground)",
  marginBottom: 16,
};

const eyebrow: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".06em",
  color: "var(--muted-foreground)",
  marginBottom: 8,
};

function platformColor(modelFamily?: string, platform?: string): string {
  const c = getModelColor(modelFamily || platform || "");
  return c && c !== "var(--muted-foreground)" ? c : "var(--spectral-violet)";
}
